// Общият слой за плътност на складовите списъци и таблици — ЕДНО място за
// височината на реда, вместо всеки екран да си пише `py-*`. Правилото: ред с
// данни е ЕДИН ред съдържание; вторичното (мярка, склад, „въвел", бележка)
// стои приглушено 12px на същия ред, не на втори.
//
// Стандартът (мерен в браузъра, docs/inventory.md §Плътност и сигнали):
//   ред с данни  — 34px с мишка, ≥44px с пръст (справки, списъци, редове с контроли)
//   заглавен ред — 30px
//   ред „Общо"   — 36px
// Височината идва от `min-h-*`, НЕ от отстъпите: при border-box рамката на реда
// е вътре в нея, а `py-1` оставя ~25px за съдържанието — текстът (20px; смесени
// шрифтове в един ред дават 22px), хапчето (20px) и малкият контрол (24px) се
// събират, без редът да расте. С отстъпи (`py-[7px]`) редът растеше с всеки по-
// висок елемент (37–39px). Съдържание над 25px е грешка на реда, не на стандарта:
// контролите в реда са малки (antd `size="small"`, `ROW_BUTTON`, `ROW_ICON_BUTTON`).
// Редът е flex или grid с `items-center` — съдържанието стои в средата.
//
// Тъч: `pointer-coarse:` уголемява реда и бутоните само с пръст (таблет в
// landscape е „десктоп" за useViewport, но пръстът пак иска ≥44px ред);
// с мишка редът остава нисък. Без JS — CSS вариантът стига.
// antd контролите в ниските редове са size="small" (24px); тяхното тъч
// уголемяване е scoped CSS `@media (pointer: coarse)` в картата, която ги носи
// (вътрешните `.ant-*` класове са недостижими за Tailwind).

// Ред с данни — справки, списъци, редове с контроли (поле/селект/бутон).
// `ROW_HEIGHT` + `ROW_INNER` поотделно — за ред, чиято височина е на обвивката
// (с линията и менюто „⋯"), а отстъпът и текстът — на бутона вътре
export const ROW_HEIGHT = "min-h-[34px] pointer-coarse:min-h-11";
// С пръст контролите растат до 36px, затова отстъпът става 3px: 1 (линията) +
// 3 + 36 + 3 = 43 → редът остава точно 44px (с `py-1` ставаше 45px)
export const ROW_INNER = "py-1 text-[13px] leading-5 pointer-coarse:py-[3px]";
export const ROW_READ = `${ROW_HEIGHT} ${ROW_INNER}`;

// Ред с контроли — СЪЩИЯТ ред като за четене (единен стандарт); името остава,
// за да личи, че контролите вътре са малки
export const ROW_DENSE = ROW_READ;

// Заглавен ред на колоните (30px) и текстът му — едни и същи навсякъде
export const ROW_HEADER = "min-h-[30px] py-1 leading-4";
export const ROW_HEADER_TEXT =
  "text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted";

// Редът „Общо" (36px)
export const ROW_TOTAL = "min-h-9 py-1 text-[13px] leading-5";

// Заглавието на карта с таблица (името и броя позиции) — един нисък ред
export const CARD_HEAD = "px-3.5 py-2";

// Иконен бутон в нисък ред („⋯", ✕): 24×28 с мишка, 36×40 с пръст
export const ROW_ICON_BUTTON = "h-6 w-7 pointer-coarse:h-9 pointer-coarse:w-10";

// Текстов бутон в нисък ред („Печат", „Продължи", „Фактурирай") — СЛЕД
// SECONDARY_BUTTON_CLASS (`!` бие отстъпите му): 24px с мишка, 36px с пръст
export const ROW_BUTTON =
  "!h-6 !px-2.5 !py-0 !text-xs pointer-coarse:!h-9 pointer-coarse:!px-3";

// ─── Работен лист (решетка с колонни линии) ─────────────────────────────────
// Клетките опират в линиите на реда, затова отстъпите са в клетките, а
// височината — на реда. Същите числа като горе: ред 34px (≥44px с пръст),
// заглавие 30px, „Общо" 36px. Полето в клетка (`p-[2px]`) е `h-full` —
// разпъва се с реда и с пръст расте заедно с него.
export const SHEET_ROW = ROW_HEIGHT;
export const SHEET_HEADER_ROW = "min-h-[30px]";
export const SHEET_HEADER_CELL = `flex min-w-0 items-center px-2.5 py-1 ${ROW_HEADER_TEXT}`;
export const SHEET_TOTAL_ROW = "min-h-9";
// Името на продукта и вторият, приглушен ред под него (наличност, цена):
// двата реда се събират в 34px (16 + 13 + 2×2 + рамката)
export const SHEET_NAME_CELL = "flex min-w-0 flex-col justify-center px-[11px] py-0.5";
export const SHEET_NAME_TEXT = "truncate text-[13px] font-semibold leading-4 text-ink";
export const SHEET_META_TEXT = "truncate text-[10.5px] leading-[13px]";
// Плоско поле в клетка — колкото реда (минус рамката и p-[2px] на клетката)
export const SHEET_INPUT = "h-full min-h-[29px] py-0";
// Отвореният редактор в клетка (antd Select/DatePicker на PickCell) — същите
// 29px (antd `controlHeight`); иначе 40-те пиксела от темата на листа вдигат реда
export const SHEET_CONTROL_HEIGHT = 29;
